import { useMemo } from 'react'
import { useReadContracts, type UseReadContractsParameters } from 'wagmi'
import { Address } from 'viem'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { BORROW_VAULT_CHAIN_ID } from './useVaultList'
import { keepPreviousData } from '@tanstack/react-query'
import { ONCHAIN_GC_TIME, ONCHAIN_REFETCH_INTERVAL, ONCHAIN_STALE_TIME, STRUCTURAL_GC_TIME, STRUCTURAL_REFETCH_INTERVAL, STRUCTURAL_STALE_TIME } from '@/config/consts'

export interface VaultData {
  vault: Address
  collateralLTVInfo: Array<{
    collateral: Address
    borrowLtvRaw: bigint
    liquidationLtvRaw: bigint
  }>
  irmInfo?: {
    interestRateInfo?: Array<{
      cash: bigint
      borrows: bigint
      borrowAPY: bigint
      supplyAPY: bigint
    }>
  }
}

interface UseVaultsParams {
  queries: Array<{
    address: Address
    refetchOnMount?: boolean
  }>
}

export function useVaults({ queries }: UseVaultsParams) {
  // Fetch LTVList for each vault
  const ltvListContracts = useMemo(
    () =>
      queries.map(({ address }) => ({
        address,
        abi: eVaultImplementationAbi,
        functionName: 'LTVList' as const,
        chainId: BORROW_VAULT_CHAIN_ID,
      })) satisfies UseReadContractsParameters['contracts'],
    [queries],
  )

  const { data: ltvLists, isSuccess: ltvListsLoaded } = useReadContracts({
    contracts: ltvListContracts,
    query: {
      enabled: queries.length > 0,
      staleTime: STRUCTURAL_STALE_TIME,
      gcTime: STRUCTURAL_GC_TIME,
      refetchInterval: STRUCTURAL_REFETCH_INTERVAL,
      placeholderData: keepPreviousData,
    },
  })

  // For each vault, fetch LTV configs for each collateral
  const collateralAddresses = useMemo(
    () =>
      ltvListsLoaded && ltvLists
        ? ltvLists.flatMap((result, idx) => {
            const ltvList = result?.result as Address[] | undefined
            const vaultAddress = queries[idx]?.address
            if (!ltvList || !vaultAddress) return []
            return ltvList.map((collateral) => ({ vault: vaultAddress, collateral }))
          })
        : [],
    [ltvListsLoaded, ltvLists, queries],
  )

  const ltvConfigContracts = useMemo(
    () =>
      collateralAddresses.flatMap(({ vault, collateral }) => [
        {
          address: vault,
          abi: eVaultImplementationAbi,
          functionName: 'LTVFull' as const,
          args: [collateral] as const,
          chainId: BORROW_VAULT_CHAIN_ID,
        },
      ]) satisfies UseReadContractsParameters['contracts'],
    [collateralAddresses],
  )

  const { data: ltvConfigs } = useReadContracts({
    contracts: ltvConfigContracts,
    query: {
      // Only enable when ltvLists is loaded AND we have collateral addresses
      enabled: ltvListsLoaded && collateralAddresses.length > 0,
      staleTime: STRUCTURAL_STALE_TIME,
      gcTime: STRUCTURAL_GC_TIME,
      refetchInterval: STRUCTURAL_REFETCH_INTERVAL,
      placeholderData: keepPreviousData,
    },
  })

  // Fetch interest rate info for each vault
  const interestRateContracts = queries.flatMap(({ address }) => [
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalAssets' as const,
      chainId: BORROW_VAULT_CHAIN_ID,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalBorrows' as const,
      chainId: BORROW_VAULT_CHAIN_ID,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'interestRate' as const,
      chainId: BORROW_VAULT_CHAIN_ID,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'interestFee' as const,
      chainId: BORROW_VAULT_CHAIN_ID,
    },
  ]) satisfies UseReadContractsParameters['contracts']

  const { data: interestRateData } = useReadContracts({
    contracts: interestRateContracts,
    query: {
      enabled: queries.length > 0,
      staleTime: ONCHAIN_STALE_TIME,
      gcTime: ONCHAIN_GC_TIME,
      refetchInterval: ONCHAIN_REFETCH_INTERVAL,
      placeholderData: keepPreviousData,
    },
  })

  // Build vault data (React Query handles caching via placeholderData)
  const vaultsData = queries.map(({ address }, vaultIdx) => {
    const ltvList = ltvLists?.[vaultIdx]?.result as Address[] | undefined
    if (!ltvList) return null

    const collateralLTVInfo = ltvList.map((collateral) => {
      const configIdx = collateralAddresses.findIndex((ca) => ca.vault === address && ca.collateral === collateral)
      // LTVFull returns: borrowLTV (uint16), liquidationLTV (uint16), initialLiquidationLTV (uint16), targetTimestamp (uint48), rampDuration (uint32)
      // wagmi returns small integers as numbers, not bigints
      const config = ltvConfigs?.[configIdx]?.result as readonly [number, number, number, bigint, number] | undefined

      if (!config) {
        return {
          collateral,
          borrowLtvRaw: 0n,
          liquidationLtvRaw: 0n,
        }
      }

      const [borrowLTV, liquidationLTV] = config
      return {
        collateral,
        borrowLtvRaw: borrowLTV ? BigInt(borrowLTV) : 0n,
        liquidationLtvRaw: liquidationLTV ? BigInt(liquidationLTV) : 0n,
      }
    })

    const totalAssets = interestRateData?.[vaultIdx * 4]?.result as bigint | undefined
    const totalBorrows = interestRateData?.[vaultIdx * 4 + 1]?.result as bigint | undefined
    const interestRate = interestRateData?.[vaultIdx * 4 + 2]?.result as bigint | undefined
    const interestFee = (interestRateData?.[vaultIdx * 4 + 3]?.result as bigint | undefined) || 0n

    const cash = (totalAssets || 0n) - (totalBorrows || 0n)
    const borrows = totalBorrows || 0n
    const borrowAPYRaw = interestRate || 0n

    // Calculate APY from interest rate with proper interest fee adjustment
    const borrowAPY = borrowAPYRaw ? (Number(borrowAPYRaw) / 1e27) * 100 : undefined
    const totalAssetsCalc = cash + borrows
    const utilization = totalAssetsCalc > 0n ? Number((borrows * 10000n) / totalAssetsCalc) / 100 : undefined
    // Apply interest fee: supplyAPY = borrowAPY * (utilization / 100) * (1 - interestFee/10000)
    const feeMultiplier = 1 - Number(interestFee) / 10000
    const supplyAPY = borrowAPY && utilization ? borrowAPY * (utilization / 100) * feeMultiplier : undefined

    const vaultData: VaultData = {
      vault: address,
      collateralLTVInfo,
      irmInfo: {
        interestRateInfo: [
          {
            cash,
            borrows,
            borrowAPY: borrowAPYRaw,
            supplyAPY: BigInt(Math.round((supplyAPY || 0) * 1e18)),
          },
        ],
      },
    }

    return { data: vaultData }
  })

  return vaultsData.filter((v) => v !== null) as Array<{ data: VaultData }>
}
